import React, { useEffect, useState, ChangeEvent } from 'react'
import { Pagination } from 'react-bootstrap'
import { useQueryClient } from '@tanstack/react-query'
import { DEFAULT_FILTER, HEROES_PAGE_LIMIT } from '../../constants'
import useAdminStore from '../../store/AdminStore'
import { fetchHeroes } from '../../network/HeroesApi'
import './AdminHeroes.scss'

interface CatalogueHero {
  _id: string
  name?: string
  avatar?: string
  color?: string
  pieceType?: string
  heroPower?: string
  sale?: string
  [key: string]: unknown
}

const AdminHeroes = () => {
  const [page, setPage] = useState(0)
  const [pagesArray, setPagesArray] = useState<number[]>([])
  const queryClient = useQueryClient()
  const { updateHero } = useAdminStore()
  const [heroes, setHeroes] = useState<CatalogueHero[]>([])

  useEffect(() => {
    return () => {
      queryClient.cancelQueries({ queryKey: ['HEROES'] })
    }
  }, [])

  const { isLoading, data } = fetchHeroes(page, HEROES_PAGE_LIMIT, DEFAULT_FILTER)

  useEffect(() => {
    if (Array.isArray(data?.data)) {
      const total = typeof data.totalCount === 'number' ? data.totalCount : 0
      setPagesArray([...Array(Math.ceil(total / HEROES_PAGE_LIMIT) || 1).keys()])
      setHeroes(data.data as CatalogueHero[])
    }
  }, [data?.data?.length])

  const handleSaleClick = async (e: ChangeEvent<HTMLInputElement>, item: CatalogueHero) => {
    const newSale = e.target.checked ? 'forsale' : 'notforsale'
    setHeroes(prev => prev.map(obj => (obj._id === item._id ? { ...item, sale: newSale } : obj)))
    try {
      await updateHero(item._id, { _id: item._id, sale: newSale })
    } catch {
      setHeroes(prev => prev.map(obj => (obj._id === item._id ? item : obj)))
    }
  }

  return (
    <div className="heroes-page">
      <div className="heroes-header">
        <div>
          <h3 className="heroes-title">Heroes</h3>
          {!isLoading && data?.totalCount != null && (
            <span className="heroes-count">{data.totalCount} heroes</span>
          )}
        </div>
      </div>

      <div className="heroes-card">
        {isLoading && !heroes.length ? (
          <div className="heroes-loading">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="heroes-skel-row">
                <div className="heroes-skel-img" />
                <div className="heroes-skel-lines">
                  <div className="heroes-skel-line w50" />
                  <div className="heroes-skel-line w30" />
                </div>
              </div>
            ))}
          </div>
        ) : heroes.length ? (
          <div className="heroes-table-wrap">
            <table className="heroes-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Hero</th>
                  <th>ID</th>
                  <th>Color</th>
                  <th>Piece Type</th>
                  <th>Hero Power</th>
                  <th>For Sale</th>
                </tr>
              </thead>
              <tbody>
                {heroes.map((item, index) => (
                  <tr key={item._id || index}>
                    <td className="col-num">{page * HEROES_PAGE_LIMIT + index + 1}</td>
                    <td className="col-hero">
                      <div className="hero-cell">
                        {item.avatar ? (
                          <img className="hero-thumb" src={item.avatar} alt={item.name} />
                        ) : (
                          <div className="hero-thumb-placeholder">♟</div>
                        )}
                        <span className="hero-name">{item.name}</span>
                      </div>
                    </td>
                    <td className="col-id">
                      <span className="heroes-id">{item._id}</span>
                    </td>
                    <td>
                      <span className={`color-badge color-${item.color}`}>
                        {item.color === 'w' ? 'White' : item.color === 'b' ? 'Black' : item.color}
                      </span>
                    </td>
                    <td>
                      <span className="piece-type-badge">{item.pieceType?.toUpperCase()}</span>
                    </td>
                    <td className="col-power">{item.heroPower || '—'}</td>
                    <td>
                      <label
                        className="sale-toggle"
                        title={item.sale === 'forsale' ? 'Remove from sale' : 'Put for sale'}
                      >
                        <input
                          type="checkbox"
                          checked={item.sale === 'forsale'}
                          onChange={e => handleSaleClick(e, item)}
                        />
                        <span className="toggle-track">
                          <span className="toggle-thumb" />
                        </span>
                        <span className={`sale-label ${item.sale === 'forsale' ? 'on' : 'off'}`}>
                          {item.sale === 'forsale' ? 'Listed' : 'Unlisted'}
                        </span>
                      </label>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="heroes-empty">
            <span>♟</span>
            <p>No Heroes found</p>
          </div>
        )}

        {pagesArray.length > 1 && (
          <div className="heroes-pagination">
            <Pagination>
              <Pagination.Prev disabled={page === 0} onClick={() => setPage(p => p - 1)} />
              {pagesArray.map(obj => (
                <Pagination.Item key={obj} active={obj === page} onClick={() => setPage(obj)}>
                  {obj + 1}
                </Pagination.Item>
              ))}
              <Pagination.Next
                disabled={page === pagesArray.length - 1}
                onClick={() => setPage(p => p + 1)}
              />
            </Pagination>
          </div>
        )}
      </div>
    </div>
  )
}

export default AdminHeroes
